<template>
  <div class="electronic-order">

    <MoleculesGlobalTabs :current-index="step" :max-enabled-index="maxEnabledIndex" :last-tab-inactive="true" :show-index="true" :tabs="tabs" @tab-selected="(index) => goToStep(index)" />

    <div class="electronic-order__wrapper">

      <!-- step 1 - summary -->

      <template v-if="step === 0">

        <!-- item to buy -->

        <MoleculesBlocksCartItem :imageId="props.wareImageId" :name="props.wareName" :variant-name="props.variantName" :piecePriceFormatted="locale.formatPrice(props.piecePrice)" :totalPrice="totalPrice || 0" :unit="props.unit" :hide-remove-button="true" v-model="quantity" />

        <!-- payment methods -->

        <strong class="block mb-8 text-lg tracking-normal">{{ $t('Vyberte si způsob platby') }}</strong>

        <OrganismsPanelsPaymentSelector :options="payments" v-model="selectedPaymentData" />

        <!-- buttons -->

        <div class="flex flex-wrap justify-between gap-4 mt-0 pt-4 border-t border-neutral-300 tablet:pt-6 desktop:pt-8">
          <MoleculesButtonsButton :arrow-left="true" :ghost="true" @click="close" class="w-full order-1 tablet:w-auto">{{ $t('Zpět na produkt') }}</MoleculesButtonsButton>
          <MoleculesButtonsButton :arrow-right="true" @click="goToStep(1)" class="w-full order-0 tablet:w-auto tablet:order-2">{{ $t('Pokračovat k platbě') }}</MoleculesButtonsButton>
        </div>

      </template>

      <!-- step 2 - user -->

      <template v-else-if="step === 1">

        <!-- login -->

        <div class="mb-6">
          <div v-if="!useCustomer().isLogged()">
            <AtomsImagesIcon icon="user" class="mr-4" /> {{ $t('Už jsem tady nakupoval a ') }} <button class="underline text-primary-500" @click="useDialog().open('login');">{{ $t('chci se přihlásit') }}</button>
          </div>
          <LazyOrganismsDialogsLogin @logged="logged" />
        </div>

        <div class="pb-4">
          <AtomsFormsCheckbox v-model="customerData.companyChecked" :label="i18n.t('Nakupuji na firmu')" />
        </div>

        <!-- contact data -->

        <MoleculesFormsValidatedField type="email" v-model="customerData.email" :label="i18n.t('Zadejte Váš e-mail')" :required="true" :placeholder='i18n.t(`Například "jan.novak(at)gmail.com"`)' :error-message="i18n.t('Zadejte e-mail ve správném formátu')" :ref="el => vgOrder[0] = el" />
        <MoleculesFormsValidatedField v-model="customerData.address.name" :label="i18n.t('Jméno a příjmení')" :max-length="50" :required="true" :placeholder='i18n.t(`Například "Jan Novák"`)' :error-message="i18n.t('Zadejte jméno i příjmení')" :ref="el => vgOrder[1] = el" />
        <MoleculesFormsValidatedField type="tel" v-model="customerData.phone" :country-code="selectedCountry.code" :label="i18n.t('Telefon')" :required="true" :placeholder='i18n.t(`Zadejte bez předvolby a mezer, například: "777888999"`)' :error-message='i18n.t(`Zadejte bez předvolby a mezer, například: "777888999"`)' :ref="el => vgOrder[2] = el" />

        <!-- company -->

        <MoleculesContainersSlideToggle :expanded="customerData.companyChecked">
          <MoleculesFormsValidatedField v-model="customerData.billing.company" :label="i18n.t('Název firmy')" :required="true" :placeholder="i18n.t('Zadejte název firmy')" :error-message="i18n.t('Zadejte název firmy')" :ref="el => vgCompany[0] = el" />
          <MoleculesFormsValidatedField type="ic" v-model="customerData.ic" :label="i18n.t('IČO')" :required="selectedCountry.code === 'CZ'" :country-code="selectedCountry.code" :placeholder="i18n.t('Zadejte IČO firmy (8 čísel bez mezer)')" :error-message="i18n.t('Zadejte platné IČO')" :ref="el => vgCompany[1] = el" />
          <template v-if="selectedCountry.code !== 'CZ'">
            <AtomsFormsCheckbox v-model="customerData.vatPayerChecked" :label="i18n.t('Jsem plátce DPH')" />
            <MoleculesContainersSlideToggle :expanded="customerData.vatPayerChecked" class="mb-6">
              <MoleculesFormsValidatedField type="vatId" v-model="customerData.vatId" :label="i18n.t('DIČ')" :placeholder="i18n.t('Zadejte DIČ firmy')" :error-message="i18n.t('Zadejte platné DIČ ve formátu například: hint', { hint: selectedCountry.vatIdHint })" :ref="el => vgCompany[2] = el" />
            </MoleculesContainersSlideToggle>
          </template>
          <template v-else>
            <MoleculesFormsValidatedField type="vatId" v-model="customerData.vatId" :label="i18n.t('DIČ')" :placeholder="i18n.t('Zadejte DIČ firmy')" :error-message="i18n.t('Zadejte platné DIČ ve formátu například: hint', { hint: selectedCountry.vatIdHint })" :ref="el => vgCompany[2] = el" />
          </template>
        </MoleculesContainersSlideToggle>

        <!-- register -->

        <div class="mb-6">
          <div v-if="!useCustomer().isLogged()">
            <AtomsFormsCheckbox v-model="customerData.registerChecked" :label="i18n.t('Chci se registrovat')" class="font-bold" /> ({{ $t('příště ušetříte čas s vyplňováním některých údajů') }})
          </div>

          <MoleculesContainersSlideToggle :expanded="customerData.registerChecked">
            <MoleculesFormsValidatedField type="newPassword" v-model="password" :required="true" :label="i18n.t('Heslo')" :placeholder="i18n.t('Zadejte heslo (alespoň 8 znaků)')" :ref="el => vgRegister[0] = el" />
            <MoleculesFormsValidatedField type="passwordCheck" v-model="passwordCheck" :required="true" :password-to-check="password" :label="i18n.t('Stejné heslo znovu')" :placeholder="i18n.t('Zadejte stejné heslo (pro kontrolu)')" :error-message="i18n.t('Zadaná hesla se neshodují')" :ref="el => vgRegister[1] = el" />
          </MoleculesContainersSlideToggle>
        </div>

        <div class="mt-4 py-4 border-t border-neutral-300 space-y-4">

          <!-- reviews rejection -->

          <div v-if="!shopZone.disabledFeatures.includes('ORDER_REVIEW_CHECKBOX')">
            <AtomsFormsCheckbox v-model="customerData.rejectReviewChecked" :label="i18n.t('Nechci vyplňovat dotazník spokojenosti s nákupem')" />
          </div>

          <!-- newsletter -->

          <div>
            <AtomsFormsCheckbox v-model="customerData.newsletterChecked">
              {{ $t('Přeji si dostávat informace o novinkách a akčních nabídkách') }}
              <div class="mt-2 max-w-[350px] text-sm text-neutral-300">
                {{ $t('Souhlasím se zpracováním mých osobních údajů (jméno, e-mailová adresa, telefonní číslo) prodávajícím Safety Agency, s.r.o. pro marketingové účely. Vyjádření souhlasu je dobrovolné. Mám právo svůj souhlas kdykoliv odvolat.') }}
              </div>
            </AtomsFormsCheckbox>
          </div>

          <!-- legal -->

          <div class="max-w-[440px] ml-auto text-right">
            <div class="pt-2">{{ $t('Prohlašuji, že jsem se seznámil se zásadami pro ') }}
              <button class="underline text-primary-500" @click="dialog.open('iframe', { src: i18n.t('https://cdn.rigad.cz/shopFiles/documents/zpracovani-osobnich-udaju.html') })">{{ $t('zpracování osobních údajů') }}</button>
              {{ $t('i s') }}
              <button class="underline text-primary-500" @click="dialog.open('iframe', { src: i18n.t('https://cdn.rigad.cz/shopFiles/documents/obchodni-podminky.html') })">{{ $t('obchodními podmínkami') }}</button> {{ $t('a chci objednávku odeslat') }}.
              <LazyOrganismsDialogsIframeDialog />
            </div>
          </div>

        </div>

        <!-- buttons -->

        <div class="flex flex-wrap justify-between gap-4 mt-0 pt-4 border-t border-neutral-300 tablet:pt-6 desktop:pt-8">
          <MoleculesButtonsButton :arrow-left="true" :ghost="true" @click="goToStep(0)" class="w-full order-1 tablet:w-auto">{{ $t('Zpět na přehled') }}</MoleculesButtonsButton>
          <MoleculesButtonsButton :arrow-right="true" @click="placeOrder" class="w-full order-0 tablet:w-auto tablet:order-2">{{ $t('Objednám a zaplatím') }}</MoleculesButtonsButton>
        </div>

      </template>
    </div>

    <!-- loader -->

    <div class="absolute left-0 right-0 top-0 bottom-0 z-[2] flex items-start justify-center pt-[200px] pb-[200px] text-[3rem] bg-neutral-50 pointer-events-none opacity-0 transition-all duration-300" :class="{ 'opacity-90 pointer-events-auto': loading }">
      <div class="sticky top-[350px] text-center">
        <AtomsGlobalLoader class="inline-block" />
        <div class="mt-4 text-2xl font-bold">
          {{ $t('Vyčkejte prosím, objednávka se odesílá...') }}
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>

const props = defineProps({
  wareImageId: Number,
  wareName: String,
  variantId: Number,
  variantName: String,
  piecePrice: Number,
  unit: String,
  quantity: Number
});

const appConfig = useAppConfig();
const locale = useLocale();
const customer = useCustomer();
const dialog = useDialog();
const form = useForm();
const shopZone = useShopZone();
const i18n = useI18n();
const cache = useCache();
const message = useMessage();

const emit = defineEmits(['close']);

const step = ref(0);

const quantity = ref(props.quantity);
const totalPrice = computed(() => quantity.value * props.piecePrice);

watch(() => props.quantity, (value) => {
  quantity.value = value;
});

const inputCartItem = computed(() => {
  return {
    id: 'ELECTRONIC_ITEM',
    idParent: null,
    idVariant: props.variantId,
    isVirtual: false,
    count: quantity.value,
    countForCustomPrice: 0
  }
});

const selectedCountry = (await useApiFetch(`/api/countries?countryIds=${shopZone.value.countryIds[0]}&languageId=${locale.getLocale().languageId}&currencyId=${locale.getLocale().currencyId}`)).country[0];

const shippingLevels = ref([]);

const specificWareDataForDeliveryComputation = computed(() => {
  return {
    id: '0',
    idVariant: props.variantId,
    isVirtual: false,
    count: 1,
    stored: true,
    storedOnOwnStore: true,
    deliveryPeriod: 0,
    preorder: false,
    override30days: false,
  }
});

const loadShippingLevels = async () => {
  const _shippingLevels = (await useApiFetch(`/api/shippingLevels?countryId=${shopZone.value.countryIds[0]}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`, {
    method: 'POST',
    body: {
      items: [inputCartItem.value]
    }
  })).shippingLevels;

  await useAvailability().appendDatesToShippingLevels(_shippingLevels, specificWareDataForDeliveryComputation.value);

  shippingLevels.value = _shippingLevels;
}

watch(() => inputCartItem.value.idVariant, () => {
  if (inputCartItem.value.idVariant) {
    loadShippingLevels();
    loadPayments();
  }
}, { deep: true });

//#region payment methods

const payments = ref([]);

const loadPayments = async () => {
  payments.value = (await useApiFetch(`/api/payments?shippingIds=${appConfig.electronicShippingId}&countryId=${shopZone.value.countryIds[0]}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`, {
    method: 'POST',
    body: {
      items: [inputCartItem.value]
    }
  })).payments;
}

const selectedPaymentData = ref(null);

//#endregion

//#region customer data

const vgOrder = [];
const vgCompany = [];
const vgRegister = [];

const logged = async () => {
  await useCustomer().initializeCustomer(true);
  customerData.value = useCookie('customerData').value;
  useDialog().close('login');

  await loadPayments();

  if (!payments.value.some(payment => payment.id === selectedPaymentData.value.id)) {
    selectedPaymentData.value = null;
    message.show(i18n.t('Prosíme, zvolte znovu způsob platby'), null, true);
    goToStep(0);
  }
}

const customerDataCookie = useCookie('customerData');

const customerData = ref(customerDataCookie.value || {
  email: '',
  phone: '',
  address: {
    name: '',
    company: '',
    street: '',
    city: '',
    zip: ''
  },
  billing: {
    name: '',
    company: '',
    street: '',
    city: '',
    zip: ''
  },
  ic: '',
  vatId: '',
  companyChecked: false,
  vatPayerChecked: false,
  registerChecked: false,
  billingAddressChecked: false,
  rejectReviewChecked: false,
  newsletterChecked: true,
});

const password = ref(null);
const passwordCheck = ref(null);

//#endregion

//#region validate & place order

const loading = ref(false);

const validateForm = async () => {

  let result = true;
  const scrollOffset = null;

  if (customerData.value.registerChecked) {
    result &= await form.validateGroup(vgRegister, scrollOffset);
  }

  if (customerData.value.companyChecked) {
    result &= await form.validateGroup(vgCompany, scrollOffset);
  }

  result &= await form.validateGroup(vgOrder, scrollOffset);

  if (!result) {
    message.show(i18n.t('Vyplňte prosím všechny povinné údaje'), null, true);
  }

  return result;
}

const placeOrder = async () => {

  loading.value = true;

  if (await validateForm()) {

    const additionalData = [{
      type: 'NEWSLETTER',
      value: customerData.value.newsletterChecked.toString()
    }];

    if (customerData.value.registerChecked) {
      additionalData.push({
        type: 'PASSWORD',
        value: password.value
      });
    }

    if (customerData.value.rejectReviewChecked) {
      additionalData.push({
        type: 'HEUREKA',
        value: 'false'
      });
    }

    const orderData = {
      email: customerData.value.email,
      phone: customerData.value.phone,
      shippingAddress: {
        ...customerData.value.address,
        idCountry: selectedCountry.id
      },
      billingAddress: customerData.value.billingAddressChecked ? {
        ...customerData.value.billing,
        idCountry: selectedCountry.id
      } : {
        ...customerData.value.address,
        idCountry: selectedCountry.id
      },
      ic: customerData.value.companyChecked ? customerData.value.ic : null,
      dic: customerData.value.companyChecked ? customerData.value.vatId : null,
      additionalData: additionalData,
      coupon: null,
      note: null,
      browser: navigator.userAgent,
      shipping: {
        idShippingLevel: shippingLevels.value[0].id,
      },
      payment: {
        id: selectedPaymentData.value.id
      },
      expectedExpedition: shippingLevels.value[0].deliveryDate,
      items: [inputCartItem.value],
    };

    const result = (await useApiFetch(`/api/mutations/order?languageId=${locale.getLocale().languageId}&currencyId=${locale.getLocale().currencyId}`, {
      method: 'POST',
      body: {
        orderData: orderData
      }
    }));

    if (!result || result.errors) {
      loading.value = false;

      useLoader().hide();
      message.show(i18n.t('Nastala chyba při vytváření objednávky - zkuste to prosím znovu, nebo nás kontaktujte'), null, true);
    }
    else {
      document.location = result.order.createOrder.redirectUrl;
    }
  }
  else {
    loading.value = false;
  }
}

//#endregion

const close = () => {
  loading.value = false;
  emit('close');
}

const maxEnabledIndex = computed(() => selectedPaymentData.value ? 1 : 0);

const goToStep = (targetStep) => {

  if (targetStep === 1 && !selectedPaymentData.value) {
    message.show(i18n.t('Vyberte si způsob platby'), null, true);
    return;
  }

  step.value = targetStep;
}

const tabs = [
  {
    label: i18n.t('Množství a platba'),
  },
  {
    label: i18n.t('Kontaktní údaje'),
  },
  {
    label: i18n.t('Dokončení objednávky'),
  },
];

</script>
<style lang="postcss">
.electronic-order {
  @apply my-2 mt-10 border border-neutral-300 tablet:mx-4 desktop:mt-2;

  &__wrapper {
    @apply p-4 tablet:p-6 desktop:p-8;
  }

  .cart-item {

    @apply flex-wrap pb-8 mb-8 border-b border-neutral-200;

    &__image {
      @apply w-full mt-4 tablet:mt-0 tablet:w-[100px];

      img {
        @apply max-h-full mx-auto;
      }
    }

    &__description {
      @apply tablet:flex-1;
    }

    &__availability {
      @apply hidden;
    }

    &__quantity-with-price {
      @apply justify-between w-full tablet:w-auto tablet:-mr-4 tablet:flex-wrap tablet:justify-center;
    }

    &__quantity {
      @apply order-2;
    }

    &__price {
      @apply order-1 w-full min-w-[100px] tablet:text-center;

      &__piece {
        @apply hidden;
      }
    }
  }
}
</style>